import type { AnalyticsEventName, UpsellType } from '../types/events';

export type DispatcherContext = {
  sha256UserEmail: string;
  userEmail: string;
  currencyCode: string;
  clickId: string;
};

export type PlatformEventWithResolve = {
  name: string | ((props: Record<string, unknown>) => string);
  resolve?: (ctx: DispatcherContext, props: Record<string, unknown>) => Record<string, unknown>;
  filter?: (props: Record<string, unknown>) => boolean;
};

export type PlatformEntry = string | PlatformEventWithResolve;
export type PlatformConfig = PlatformEntry | PlatformEntry[];

type EventConfig = {
  suppressible?: true;
  noMixpanel?: true;
  facebook?: PlatformConfig;
  redtrack?: PlatformConfig;
  tiktok?: PlatformConfig;
  snap?: PlatformConfig;
  growthbook?: PlatformConfig;
  gtag?: PlatformConfig;
  taboola?: PlatformConfig;
};

const UPSELL_OFFERED_GROWTHBOOK_EVENTS: Record<UpsellType, string> = {
  'Fitness': 'upsale_fitness_shown',
  'Courses': 'upsale_sexology_shown',
  'Groin Fitness': 'upsale_fitness_shown',
  'Sexology Courses': 'upsale_sexology_shown',
  'Sexology Meditations': 'upsale_meditations_shown',
};

export const EVENT_ROUTING: Record<AnalyticsEventName, EventConfig> = {
  // --- suppressible via mixmin=1 ---
  'WebApp Started': { suppressible: true },
  'Start Screen Shown': { suppressible: true, facebook: 'PageView', tiktok: 'ViewContent', taboola: 'page_view' },
  'Quiz Started': { suppressible: true, facebook: 'Quiz_Started', redtrack: 'Quiz_Started' },
  'Date_1 Screen Shown': { suppressible: true },
  'Date_1 Screen Complete': { suppressible: true },
  'Date_2 Screen Shown': { suppressible: true },
  'Date_2 Screen Complete': { suppressible: true },
  'Analyzing Screen Shown': { suppressible: true },
  'Analyzing Screen Complete': { suppressible: true },
  'Plan Created Shown': { suppressible: true },
  'Health data pop-up shown': { suppressible: true },
  'Health data pop-up complete': { suppressible: true },
  'Quiz_Start_Screen_Shown': { suppressible: true, noMixpanel: true, redtrack: 'Quiz_Start_Screen_Shown' },

  // --- always fires ---
  'Bundle Downloaded': {},
  'Attribution': {},
  '$experiment_started': {},
  'Feature Flag Applied': {},
  '2nd Screen Shown': {
    noMixpanel: true,
    growthbook: '2nd_screen_shown',
  },
  'E-mail Screen Shown': {
    redtrack: 'Quiz_Finished',
    growthbook: 'email_screen_shown',
  },
  'E-mail Screen Completed': {
    facebook: ['CompleteRegistration', 'Lead'],
    tiktok: ['CompleteRegistration', 'Lead'],
    snap: {
      name: 'SUBSCRIBE',
      resolve: (ctx, props) => ({
        user_hashed_email: ctx.sha256UserEmail,
        user_email: (props.email as string) || ctx.userEmail,
      }),
    },
    redtrack: 'Lead',
    growthbook: 'email_screen_completed',
  },
  'Verify Mail Screen Shown': {},
  'Change Email Pop-up Shown': {},
  'Mail Changed Success': {},
  'Payment Email Retrieved': {},
  '404 Redirect': {},
  'Landing Page Shown': {
    growthbook: 'paywall_shown',
  },
  'Check-out Shown': {
    facebook: 'InitiateCheckout',
    tiktok: 'InitiateCheckout',
    snap: {
      name: 'ADD_CART',
      resolve: ctx => ({
        user_hashed_email: ctx.sha256UserEmail,
        currency: ctx.currencyCode,
      }),
    },
    redtrack: 'Checkout_Shown',
    growthbook: 'checkout_shown',
  },
  'Check-out Closed': {},
  'Check-out Started': {
    facebook: {
      name: 'AddPaymentInfo',
      filter: props => props.type === 'GooglePay',
    },
    tiktok: 'AddPaymentInfo',
    snap: {
      name: 'START_CHECKOUT',
      resolve: ctx => ({
        user_hashed_email: ctx.sha256UserEmail,
        currency: ctx.currencyCode,
      }),
    },
    redtrack: 'Checkout_Started',
    growthbook: {
      name: 'checkout_started',
    },
  },
  'Discount Offered': {},
  'Upsale Offered': {
    growthbook: {
      name: props => UPSELL_OFFERED_GROWTHBOOK_EVENTS[props.Type as UpsellType],
    },
  },
  'Upsale Answered': {},
  'Upsell Check-out Shown': { growthbook: 'checkout_shown' },
  'Upsell Check-out Closed': {},
  'Upsale Purchase': {
    noMixpanel: true,
    gtag: 'purchase',
  },
  'Authorization_Start': {
    facebook: {
      name: 'AddPaymentInfo',
      filter: props => props.type === 'CC',
    },
  },
  'Authorization_Success': {
    facebook: {
      name: 'AddPaymentInfo',
      filter: props => props.type === 'ApplePay' || props.type === 'PayPal',
    },
    snap: {
      name: 'PURCHASE',
      resolve: (ctx, props) => ({
        user_hashed_email: ctx.sha256UserEmail,
        price: props.price,
        currency: props.currency ?? ctx.currencyCode,
        transaction_id: `${ctx.clickId}_001`,
        client_dedup_id: `${ctx.clickId}_001`,
        ...(props.firstname ? { firstname: props.firstname } : {}),
        ...(props.lastname ? { lastname: props.lastname } : {}),
      }),
    },
    gtag: 'purchase',
  },
  'Payment duration': {},
  'Payment Error': {},
  'Payment Init': {},
  'CC Payment Attempt': {},
  'Supplement Offer Shown': { growthbook: { name: 'supp_long_shown' } },
  'Supplement CTA Tapped': { growthbook: { name: 'supp_cta_tap' } },
  'Supplement Address Shown': { growthbook: { name: 'supp_address_shown' } },
  'Supplement Address Confirmed': {
    growthbook: {
      name: 'supp_address_confirmed',
      resolve: (ctx, props) => ({ arm: props.arm, plan: props.plan }),
    },
  },
  'Supplement Sold Out Shown': { growthbook: { name: 'supp_soldout_shown' } },
  'Supplement Email Opt-in': {
    growthbook: {
      name: 'supp_email_optin',
      resolve: (ctx, props) => ({ arm: props.arm, plan: props.plan }),
    },
  },
  'Supplement Skipped': { growthbook: { name: 'supp_skip' } },
  'Download Page Shown': {},
  'Final Screen Shown': { growthbook: { name: 'final_screen_shown' } },
  'Tip Thank You Screen Show': { growthbook: { name: 'tip_thankyou_screen_shown' } },
  'Tip Option Selected': { growthbook: { name: 'tip_option_selected' } },
  'Tip Thank You Continue Tapped': { growthbook: { name: 'tip_thankyou_continue_tapped' } },
  'Web Mail LP Shown': {},
  'PayPal button no render': {},
  'applepay_payment_method_selected': {},
  'applepay_syscheeckout_closed': {},
  'applepay_onpaymentauthorized_call': {},
  'applepay_auth_success': {},
  'applepay_auth_error': {},
  'googlepay_syscheeckout_closed': {},
};
